import type {
  GA4AbnormalSearchEvent,
  GA4AddToWishlist,
  GA4BundlePromotionEvent,
  GA4Display,
  GA4HotelSearchFormFilter,
  GA4HotelSearchFormFilterChip,
  GA4InternalEvent,
  GA4MarketingPromotionEvent,
  GA4OktaEmailOTPVerification,
  GA4OnSearchButtonClickForSuccessfulSearchTrigger,
  GA4PackageCarousel,
  GA4PackageFormAdvancedSearchLink,
  GA4PackagePromoEvent,
  GA4PageViewEvent,
  GA4PennyIconDisplay,
  GA4PennyTooltipClick,
  GA4PennyTooltipDisplay,
  GA4PennyTypeahead,
  GA4SearchForm,
  GA4SearchFormModalExit,
  GA4SearchRedirectErrorDisplay,
  GA4SelectContent,
  GA4SignIn,
  GA4SocialSignIn,
  GA4TripActivity,
} from '@/ga4types'
import PCLNAnalytics from '@pcln/analytics'
import { type IGoogleAnalyticsFourMarketingTracking } from '@pcln/contentful-components'

type IGoogleAnalyticsFourMarketingTrackingNoContentId = Omit<
  IGoogleAnalyticsFourMarketingTracking,
  'contentTypeId'
>

type GA4Event =
  | GA4SignIn
  | GA4SocialSignIn
  | GA4PackageFormAdvancedSearchLink
  | GA4BundlePromotionEvent
  | GA4PackageCarousel
  | GA4TripActivity
  | GA4InternalEvent
  | GA4SearchForm
  | GA4SelectContent
  | GA4Display
  | GA4MarketingPromotionEvent
  | GA4AbnormalSearchEvent
  | GA4PennyTypeahead
  | GA4PennyIconDisplay
  | GA4PennyTooltipDisplay
  | GA4PennyTooltipClick
  | GA4PageViewEvent
  | IGoogleAnalyticsFourMarketingTrackingNoContentId
  | GA4HotelSearchFormFilterChip
  | GA4HotelSearchFormFilter
  | GA4PackagePromoEvent
  | GA4SearchFormModalExit
  | GA4AddToWishlist
  | GA4OnSearchButtonClickForSuccessfulSearchTrigger
  | GA4OktaEmailOTPVerification
  | GA4SearchRedirectErrorDisplay

type LogData = {
  message: string
  appName?: string
  appVersion?: string
  cguid?: string
  error?: unknown
  requestUrl?: string
  requestBody?: unknown
  requestMethod?: string
  responseUrl?: string
  status?: number
  statusText?: string
  type?: string
} & Record<string, unknown>

function initializePCLNAnalytics() {
  PCLNAnalytics.appName = 'next-landing'
  PCLNAnalytics.appVersion = 'unknown'
}

function fireGA4EventFromBrowser(ga4Props: Readonly<GA4Event>) {
  if (window?.dataLayer && (ga4Props.event === 'select_promotion' || ga4Props.event === 'view_promotion')) {
    window.dataLayer.push({ ecommerce: null })
  }
}

class Analytics {
  fireGA4Event(ga4Props: Readonly<GA4Event>) {
    try {
      initializePCLNAnalytics()
      PCLNAnalytics.GA4Enabled = true
      fireGA4EventFromBrowser(ga4Props)
      PCLNAnalytics.logEvent(ga4Props)
    } finally {
      PCLNAnalytics.GA4Enabled = false
    }
  }

  // Log an error
  // sample query 'index=cheetah sourcetype=ESBROWSEREVENTDATA_ESSVCS action=Error'
  logError(logData: LogData) {
    initializePCLNAnalytics()
    PCLNAnalytics.logError({
      ...logData,
    })
  }

  // Log directly to Splunk
  // sample query 'index=cheetah sourcetype=ESBROWSEREVENTDATA_ESSVCS action=Log'
  log({ message = '', ...rest }) {
    initializePCLNAnalytics()
    PCLNAnalytics.log({
      message,
      ...rest,
    })
  }

  // Log a warning
  // sample query 'index=cheetah sourcetype=ESBROWSEREVENTDATA_ESSVCS action=warning'
  logWarning({ name = '', message = '' }) {
    initializePCLNAnalytics()
    PCLNAnalytics.logWarning({
      name,
      message,
    })
  }
}

export default new Analytics()
